import { BookOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { useAccess } from '../../../lib/useAccess';
import { Section } from '../../../ui/Section';
import { EmptyState } from '../../../ui/States';
import { ModulePage } from '../../modules/pages/ModulePage';
import { KnowledgeBrowser } from '../components/KnowledgeBrowser';
import { KnowledgeDetail } from '../components/KnowledgeDetail';
import { NewNoteDialog } from '../components/NewNoteDialog';

/**
 * Wiedza (/wiedza, /wiedza/:id; Figma 10 „Notatka z decyzjami”): na desktopie lista 320 px po lewej i rekord po
 * prawej, na mobile osobno lista albo rekord (z powrotem do listy). `?nowa=1` (⌘K „Nowa notatka”) otwiera formularz.
 */
export function KnowledgePage() {
  const access = useAccess();
  if (!access.can('knowledge')) return <ModulePage module="knowledge" />;
  return <KnowledgeWorkspace />;
}

function KnowledgeWorkspace() {
  const { id } = useParams();
  const access = useAccess();
  const navigate = useNavigate();
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const canAdd = access.can('knowledge', 'edit');
  const [newOpen, setNewOpen] = useState(false);
  const workspaceLabel = (session.data?.authenticated && session.data.workspace?.name) || 'Firma';

  // ?nowa=1 (np. z ⌘K): otwórz formularz i wyczyść parametr
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy wejściu z parametrem
  useEffect(() => {
    if (params.get('nowa') !== '1') return;
    if (canAdd) setNewOpen(true);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('nowa');
        return p;
      },
      { replace: true },
    );
  }, [params.get('nowa')]);

  const listSearch = params.toString();
  return (
    <div className="md:flex md:min-h-dvh">
      <div
        className={cn(
          // desktop: lista w białej kolumnie (jak menu boczne), notatka na szarym tle w białych sekcjach
          'px-4 pt-6 pb-8 md:sticky md:top-0 md:h-dvh md:w-[320px] md:shrink-0 md:overflow-y-auto md:border-r md:border-line md:bg-panel md:px-5 md:pt-10',
          id && 'hidden md:block',
        )}
      >
        <KnowledgeBrowser
          selectedId={id ?? null}
          workspaceLabel={workspaceLabel}
          headingLevel={id ? 'h2' : 'h1'}
          onNew={canAdd ? () => setNewOpen(true) : undefined}
        />
      </div>
      <div className={cn('min-w-0 flex-1', !id && 'hidden md:block')}>
        <div className="mx-auto w-full max-w-[784px] px-4 pt-6 pb-16 md:px-8 md:pt-12">
          {id ? (
            <KnowledgeDetail
              id={id}
              workspaceLabel={workspaceLabel}
              backTo={`/wiedza${listSearch ? `?${listSearch}` : ''}`}
            />
          ) : (
            <Section>
              <EmptyState
                icon={BookOpen}
                title="Wybierz notatkę z listy"
                description="Notatki w Markdown z historią wersji, decyzje ze źródłem, linki i pliki."
                className="py-24"
              />
            </Section>
          )}
        </div>
      </div>
      {canAdd && (
        <NewNoteDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          onCreated={(item) => void navigate(`/wiedza/${item.id}${listSearch ? `?${listSearch}` : ''}`)}
        />
      )}
    </div>
  );
}
